import React from "react";
import { Box, Container, Typography, Grid, Link, IconButton } from "@mui/material";
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import "../../i8n"
import {useTranslation} from 'react-i18next'

const colors = {
  darkGreen: "#1B5E20",
  mediumGreen: "#2E7D32",
  lightGreen: "#4CAF50",
  paleGreen: "#E8F5E9",
  darkOrange: "#E65100",
  lightOrange: "#FF9800",
  white: "#FFFFFF",
  darkText: "#1a1a1a",
  subText: "#b0b0b0",
};

export default function Footer() {
  const {t} = useTranslation()
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#0d2e0f",
        color: colors.white,
        pt: 5,
        pb: 2,
        mt: "auto",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 4 }}>
          
          {/* Column 1: Menu Navigation */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.lightOrange,
                mb: 2,
                fontSize: "1.1rem",
              }}
            >
              {t('footer.quickLinks')}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {t("footer.links", { returnObjects: true }).map((link) => (
                <Link
                  href="#"
                  underline="none"
                  key={link}
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    transition: "all 0.2s ease",
                    fontWeight: 500,
                    fontSize: "0.9rem",
                    "&:hover": {
                      color: colors.lightOrange,
                      transform: "translateX(5px)",
                      display: "inline-block",
                    },
                  }}
                >
                  {link}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Column 2: Google Map & Contact Address */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.lightOrange,
                mb: 2,
                fontSize: "1.1rem",
              }}
            >
               {t('footer.contactAddressTitle')}
            </Typography>
            {/* Embedded Google Map */}
            <Box
              sx={{
                width: "100%",
                height: 140,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <iframe
                title="Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.5876486583426!2d80.27585471541368!3d13.05481691625!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5261e15e4c6b6d%3A0x7c5b1f3e0e4d8e9f!2sDepartment%20of%20Agriculture%2C%20Chepauk!5e0!3m2!1sen!2sin!4v1689939286923!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
              <LocationIcon sx={{ color: colors.lightOrange, fontSize: 18, mt: 0.3 }} />
              <Typography sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
               {t("footer.address")}
              </Typography>
            </Box>
          </Grid>

          {/* Column 3: Contact Person Information */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.lightOrange,
                mb: 2,
                fontSize: "1.1rem",
              }}
            >
           {t("footer.reachOutTitle")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PhoneIcon sx={{ color: colors.lightOrange, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.75rem", color: colors.subText, textTransform: "uppercase" }}>{t('footer.phoneLabel')}</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>(044) 28583323</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.05)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <EmailIcon sx={{ color: colors.lightOrange, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.75rem", color: colors.subText, textTransform: "uppercase" }}>{t('footer.emailLabel')}</Typography>
                  <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, wordBreak: "break-all" }}>coa.fcms@gmail.com</Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Copyright Section */}
        <Box
          sx={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            pt: 2,
            pb: 1,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
            © 2026 Agricuture. All rights reserved | Design by IT-Team Department of Agriculture
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}